import { createEmitter } from "../../Utilities/events";
import {
  CastingCapabilityState,
  CastingConnectionState,
  CastingEvent,
  CastingTarget,
  CastingUiState,
} from "./CastingEvents";
import { createChromecastProvider } from "./ChromecastProvider";
import { createAirPlayProvider } from "./AirPlayProvider";
import { createRokuProvider } from "./RokuProvider";
import { createFireTVProvider } from "./FireTVProvider";
import { createSamsungTVProvider } from "./SamsungTVProvider";
import { createLGTVProvider } from "./LGTVProvider";
import { createFutureNativeProvider } from "./FutureNativeProvider";
import { createSimulatedCastProvider } from "./SimulatedCastProvider";
import { createNullCastingProvider } from "./NullCastingProvider";
import { normalizeWhiteLabelCastConfig } from "./CastReceiverContract";
import { createScreenMirrorProvider } from "./ScreenMirrorProvider";

/**
 * CastingManager — provider selection, session tracking, local↔remote sync,
 * unexpected-disconnect failover. PlaybackEngine stays cast-SDK free.
 *
 * App-level ownership: engine / getMediaDescriptor are attachable so the
 * Cast session survives VideoPlayer unmount. Content handoff uses loadMedia
 * on the existing remote session.
 *
 * Provider-neutral: Chromecast / AirPlay / Roku / Fire TV / Samsung / LG /
 * FutureNative / Screen Mirroring plug the same host. Only operational
 * providers report available cast devices — receiver-required platforms never
 * fake discovery. Screen Mirror is a separate capability (never a fake device).
 *
 * Flow: discovery → devices → protocol → session (Cast).
 * Screen Mirror: detect → native bridge or OS handoff (never getDisplayMedia).
 *
 * @param {object} options
 * @param {object} [options.engine]
 * @param {object} [options.tracks] TrackController
 * @param {function} [options.getMediaDescriptor] () => { src, title, poster, ... }
 * @param {object} [options.features]
 * @param {object} [options.casting] white-label casting config
 */
export function createCastingManager(options) {
  options = options || {};
  var engine = options.engine || null;
  var tracks = options.tracks || null;
  var getMediaDescriptor = options.getMediaDescriptor || function () {
    return {};
  };
  var features = options.features || {};
  var castingConfig = options.casting || {};
  var whiteLabel = normalizeWhiteLabelCastConfig(
    castingConfig.whiteLabel || castingConfig
  );
  var emitter = createEmitter();
  var providers = [];
  var activeProvider = null;
  var target = CastingTarget.LOCAL;
  var lastRemoteState = null;
  var lastError = null;
  var connecting = false;
  var hasLoadedMedia = false;
  var providerUnsubs = [];
  var eventLog = [];
  var screenMirror =
    features.screenMirror === false || castingConfig.screenMirror === false
      ? null
      : createScreenMirrorProvider(
          castingConfig.screenMirrorOptions || options.screenMirrorOptions || {}
        );

  function providerEnabled(id) {
    var supported = whiteLabel.supportedCastProviders || [];
    if (supported.length && supported.indexOf(id) === -1) {
      // Still allow chromecast/airplay when list omitted legacy keys
      if (id === "chromecast" || id === "airplay") {
        return castingConfig[id] !== false && features[id] !== false;
      }
      return false;
    }
    if (features[id] === false || castingConfig[id] === false) {
      return false;
    }
    return true;
  }

  function log(type, detail) {
    var entry = {
      at: new Date().toISOString(),
      type: type,
      detail: detail || null,
    };
    eventLog = [entry].concat(eventLog).slice(0, 80);
    emitter.emit(CastingEvent.STATE_SYNC, { logOnly: true, entry: entry });
  }

  function emit(type, payload) {
    log(type, payload);
    emitter.emit(type, payload);
  }

  function buildProviders() {
    var list = [];
    var wlOpts = { whiteLabel: whiteLabel };
    if (features.casting === false) {
      return list;
    }
    if (providerEnabled("chromecast") && features.chromecast !== false && castingConfig.chromecast !== false) {
      list.push(
        createChromecastProvider(
          Object.assign({}, castingConfig.chromecastOptions || {}, {
            receiverApplicationId:
              castingConfig.receiverApplicationId ||
              whiteLabel.receiverApplicationId ||
              (castingConfig.chromecastOptions &&
                castingConfig.chromecastOptions.receiverApplicationId),
          })
        )
      );
    }
    if (providerEnabled("airplay") && features.airplay !== false && castingConfig.airplay !== false) {
      list.push(createAirPlayProvider(castingConfig.airplayOptions || {}));
    }
    if (providerEnabled("roku")) {
      list.push(
        createRokuProvider(
          Object.assign({}, wlOpts, castingConfig.rokuOptions || {})
        )
      );
    }
    if (providerEnabled("firetv")) {
      list.push(
        createFireTVProvider(
          Object.assign({}, wlOpts, castingConfig.fireTvOptions || {})
        )
      );
    }
    if (providerEnabled("samsung")) {
      list.push(
        createSamsungTVProvider(
          Object.assign({}, wlOpts, castingConfig.samsungOptions || {})
        )
      );
    }
    if (providerEnabled("lg")) {
      list.push(
        createLGTVProvider(
          Object.assign({}, wlOpts, castingConfig.lgOptions || {})
        )
      );
    }
    if (providerEnabled("future_native")) {
      list.push(
        createFutureNativeProvider(
          Object.assign({}, wlOpts, castingConfig.futureNativeOptions || {})
        )
      );
    }
    if (features.castingSimulate || castingConfig.simulate) {
      list.push(
        createSimulatedCastProvider(castingConfig.simulateOptions || {})
      );
    }
    if (!list.length && features.casting) {
      list.push(createNullCastingProvider());
    }
    return list;
  }

  function clearProviderListeners() {
    providerUnsubs.forEach(function (u) {
      if (typeof u === "function") u();
    });
    providerUnsubs = [];
  }

  function bindProvider(provider) {
    if (!provider || typeof provider.on !== "function") return;
    providerUnsubs.push(
      provider.on("availability", function (available) {
        emit(CastingEvent.AVAILABILITY_CHANGED, {
          provider: provider.name,
          available: available,
        });
        emit(CastingEvent.PROVIDERS_CHANGED, getProvidersSnapshot());
      })
    );
    providerUnsubs.push(
      provider.on("capability", function (capabilityState) {
        emit(CastingEvent.CAPABILITY_CHANGED, {
          provider: provider.name,
          capabilityState: capabilityState,
        });
        emit(CastingEvent.PROVIDERS_CHANGED, getProvidersSnapshot());
      })
    );
    providerUnsubs.push(
      provider.on("devices", function (devices) {
        emit(CastingEvent.DEVICES_CHANGED, {
          provider: provider.name,
          devices: devices,
        });
      })
    );
    providerUnsubs.push(
      provider.on("session", function (info) {
        if (info.type === "starting") {
          connecting = true;
          lastError = null;
          emit(CastingEvent.SESSION_STARTING, {
            provider: provider.name,
            info: info,
          });
        }
        if (info.type === "started") {
          connecting = false;
          target = CastingTarget.REMOTE;
          activeProvider = provider;
          emit(CastingEvent.SESSION_STARTED, {
            provider: provider.name,
          });
          emit(CastingEvent.TARGET_CHANGED, { target: target });
        }
        if (info.type === "ended") {
          connecting = false;
          handleSessionEnded(provider, info.reason);
        }
      })
    );
    providerUnsubs.push(
      provider.on("remoteState", function (remote) {
        lastRemoteState = remote;
        if (remote && remote.mediaInfo) {
          hasLoadedMedia = true;
        }
        emit(CastingEvent.STATE_SYNC, {
          provider: provider.name,
          remote: remote,
          target: target,
        });
      })
    );
    providerUnsubs.push(
      provider.on("error", function (err) {
        lastError = err;
        connecting = false;
        emit(CastingEvent.ERROR, {
          provider: provider.name,
          error: err,
        });
      })
    );
  }

  function handleSessionEnded(provider, reason) {
    var wasRemote = target === CastingTarget.REMOTE;
    var resumeAt =
      (lastRemoteState && lastRemoteState.currentTime) ||
      (engine && engine.getState().currentTime) ||
      0;
    target = CastingTarget.LOCAL;
    hasLoadedMedia = false;
    connecting = false;
    if (activeProvider === provider) {
      activeProvider = null;
    }
    emit(CastingEvent.SESSION_ENDED, {
      provider: provider.name,
      reason: reason || "ended",
    });
    emit(CastingEvent.TARGET_CHANGED, { target: target });

    if (wasRemote && reason && reason !== "user") {
      emit(CastingEvent.FAILOVER_LOCAL, {
        position: resumeAt,
        reason: reason,
      });
      if (engine) {
        try {
          engine.seek(resumeAt);
          engine.play().catch(function () {});
        } catch (e) {
          /* ignore */
        }
      }
    }
  }

  function resolveCapability(provider) {
    if (provider.getCapabilityState) {
      return provider.getCapabilityState();
    }
    if (provider.isConnected && provider.isConnected()) {
      return CastingCapabilityState.CONNECTED;
    }
    if (provider.isAvailable && provider.isAvailable()) {
      return CastingCapabilityState.AVAILABLE;
    }
    return CastingCapabilityState.DISCONNECTED;
  }

  function getProvidersSnapshot() {
    return providers.map(function (p) {
      var caps = p.getCapabilities ? p.getCapabilities() : {};
      return {
        name: p.name,
        available: !!(p.isAvailable && p.isAvailable()),
        connected: !!(p.isConnected && p.isConnected()),
        connection: p.getConnectionState
          ? p.getConnectionState()
          : CastingConnectionState.DISCONNECTED,
        capabilityState: resolveCapability(p),
        capabilities: caps,
        devices: p.getDevices ? p.getDevices() : [],
        operationalOnWeb: caps.operationalOnWeb === true,
        requiresReceiverApp: !!caps.requiresReceiverApp,
        requiresNativeApp: !!caps.requiresNativeApp,
      };
    });
  }

  function anyProviderAvailable() {
    return getProvidersSnapshot().some(function (p) {
      return p.available;
    });
  }

  /**
   * Devices actually discovered (or system-picker placeholders for real CAF/AirPlay).
   * Grouped by provider type for Universal Cast UI.
   * Includes receiver-required groups for diagnostics — UI must filter stubs out.
   */
  function getDevicesGrouped() {
    var groups = {};
    var order = [
      "chromecast",
      "airplay",
      "roku",
      "firetv",
      "samsung",
      "lg",
      "future_native",
      "simulated",
    ];
    getProvidersSnapshot().forEach(function (p) {
      var key = p.name;
      if (!groups[key]) {
        groups[key] = {
          provider: key,
          capabilityState: p.capabilityState,
          available: p.available,
          connected: p.connected,
          operationalOnWeb: p.operationalOnWeb,
          requiresReceiverApp: p.requiresReceiverApp,
          requiresNativeApp: p.requiresNativeApp,
          reason: (p.capabilities && p.capabilities.reason) || null,
          devices: [],
        };
      }
      (p.devices || []).forEach(function (d) {
        groups[key].devices.push(d);
      });
    });
    return order
      .filter(function (k) {
        return !!groups[k];
      })
      .map(function (k) {
        return groups[k];
      });
  }

  /**
   * Cast menu DEVICES section: only real discoverable / picker devices.
   * Never includes requires-receiver / requires-native stub platform rows.
   */
  function getDiscoverableDeviceGroups() {
    return getDevicesGrouped().filter(function (g) {
      return (
        g &&
        g.available &&
        !g.requiresReceiverApp &&
        !g.requiresNativeApp &&
        g.devices &&
        g.devices.length > 0
      );
    });
  }

  function localSnapshot() {
    var st = engine ? engine.getState() : {};
    var textId =
      tracks && tracks.getActiveTextTrackId
        ? tracks.getActiveTextTrackId()
        : null;
    var audioId =
      tracks && tracks.getActiveAudioTrackId
        ? tracks.getActiveAudioTrackId()
        : null;
    return {
      currentTime: st.currentTime || 0,
      duration: st.duration || 0,
      playing: st.playbackState === "playing",
      volume: st.volume,
      muted: st.muted,
      playbackRate: st.playbackRate,
      textTrackId: textId,
      audioTrackId: audioId,
    };
  }

  function mediaFromLocal() {
    var desc = getMediaDescriptor() || {};
    var snap = localSnapshot();
    var textTrack = null;
    if (tracks && snap.textTrackId) {
      var texts = tracks.getTextTracks();
      for (var i = 0; i < texts.length; i++) {
        if (texts[i].id === snap.textTrackId) {
          textTrack = texts[i];
          break;
        }
      }
    }
    return Object.assign({}, desc, snap, {
      textTrack: textTrack,
      autoplay: true,
    });
  }

  function pickPreferredProviderName() {
    var snapshot = getProvidersSnapshot();
    // Prefer real web-operational providers only
    var order = ["simulated", "chromecast", "airplay", "future_native"];
    for (var i = 0; i < order.length; i++) {
      for (var j = 0; j < snapshot.length; j++) {
        if (
          snapshot[j].name === order[i] &&
          snapshot[j].available &&
          !snapshot[j].requiresReceiverApp
        ) {
          return order[i];
        }
      }
    }
    return null;
  }

  function getUiState() {
    if (lastError && target !== CastingTarget.REMOTE && !connecting) {
      var errCode = lastError.code || (lastError.error && lastError.error.code);
      if (errCode || lastError.message) {
        if (!anyProviderAvailable() && providers.length === 0) {
          return CastingUiState.UNAVAILABLE;
        }
      }
    }
    if (connecting) {
      return CastingUiState.CONNECTING;
    }
    if (target === CastingTarget.REMOTE) {
      var remote = lastRemoteState;
      var playing =
        remote &&
        (remote.playing === true ||
          (remote.mediaInfo != null) ||
          hasLoadedMedia);
      if (playing || hasLoadedMedia) {
        return CastingUiState.CASTING;
      }
      return CastingUiState.CONNECTED;
    }
    if (!providers.length) {
      return CastingUiState.UNAVAILABLE;
    }
    if (anyProviderAvailable()) {
      return CastingUiState.AVAILABLE;
    }
    return CastingUiState.DISCONNECTED;
  }

  providers = buildProviders();

  return {
    initialize: function () {
      clearProviderListeners();
      return Promise.all(
        providers.map(function (p) {
          bindProvider(p);
          return Promise.resolve(p.initialize && p.initialize()).catch(
            function () {
              return null;
            }
          );
        })
      ).then(function () {
        emit(CastingEvent.PROVIDERS_CHANGED, getProvidersSnapshot());
      });
    },

    /**
     * Bind (or re-bind) local playback. Does not create/destroy the Cast session.
     * If already remote and new media has a src, hands content off to the receiver.
     */
    attachPlayback: function (binding) {
      binding = binding || {};
      engine = binding.engine || null;
      tracks = binding.tracks || null;
      if (typeof binding.getMediaDescriptor === "function") {
        getMediaDescriptor = binding.getMediaDescriptor;
      }
      if (binding.mediaElement && this.attachMediaElement) {
        this.attachMediaElement(binding.mediaElement);
      }
      if (target === CastingTarget.REMOTE) {
        var media = mediaFromLocal();
        if (media && media.src) {
          return this.handoffContent(media);
        }
      }
      return Promise.resolve();
    },

    /**
     * Detach local player without ending the remote Cast session.
     */
    detachPlayback: function () {
      engine = null;
      tracks = null;
      getMediaDescriptor = function () {
        return {};
      };
      return Promise.resolve();
    },

    /**
     * Load / replace media on an existing remote session (A→B→C handoff).
     */
    handoffContent: function (media) {
      media = media || mediaFromLocal();
      if (target !== CastingTarget.REMOTE || !activeProvider) {
        return Promise.reject(new Error("No active cast session for handoff"));
      }
      if (!media || !media.src) {
        return Promise.resolve(null);
      }
      if (!activeProvider.loadMedia) {
        return Promise.resolve(null);
      }
      var self = this;
      return activeProvider.loadMedia(media).then(function () {
        hasLoadedMedia = true;
        if (activeProvider.synchronizeState) {
          return activeProvider.synchronizeState(localSnapshot());
        }
        return self.getRemoteState();
      });
    },

    attachMediaElement: function (el) {
      providers.forEach(function (p) {
        if (typeof p.attachMediaElement === "function") {
          p.attachMediaElement(el);
        }
      });
    },

    getProviders: getProvidersSnapshot,

    getDevicesGrouped: getDevicesGrouped,

    getDiscoverableDeviceGroups: getDiscoverableDeviceGroups,

    getScreenMirror: function () {
      return screenMirror;
    },

    getScreenMirrorSnapshot: function () {
      return screenMirror && screenMirror.getSnapshot
        ? screenMirror.getSnapshot()
        : null;
    },

    startScreenMirror: function () {
      if (!screenMirror || !screenMirror.start) {
        return Promise.reject(new Error("Screen Mirror is not available"));
      }
      return screenMirror.start().then(function (result) {
        emit(CastingEvent.STATE_SYNC, {
          screenMirror: true,
          result: result,
        });
        return result;
      });
    },

    stopScreenMirror: function () {
      if (!screenMirror || !screenMirror.stop) {
        return Promise.resolve(null);
      }
      return screenMirror.stop().then(function (result) {
        emit(CastingEvent.STATE_SYNC, {
          screenMirror: true,
          result: result,
        });
        return result;
      });
    },

    getWhiteLabelConfig: function () {
      return Object.assign({}, whiteLabel);
    },

    getDevices: function () {
      var all = [];
      providers.forEach(function (p) {
        (p.getDevices ? p.getDevices() : []).forEach(function (d) {
          all.push(d);
        });
      });
      return all;
    },

    discover: function () {
      var self = this;
      return Promise.all(
        providers.map(function (p) {
          return p.discoverDevices ? p.discoverDevices() : Promise.resolve([]);
        })
      ).then(function () {
        var devices = self.getDevices();
        emit(CastingEvent.DEVICES_CHANGED, { devices: devices });
        return devices;
      });
    },

    getActiveProvider: function () {
      return activeProvider;
    },

    getTarget: function () {
      return target;
    },

    isRemote: function () {
      return target === CastingTarget.REMOTE;
    },

    getRemoteState: function () {
      if (activeProvider && activeProvider.getRemoteState) {
        return activeProvider.getRemoteState();
      }
      return lastRemoteState;
    },

    getUiState: getUiState,

    getSessionInfo: function () {
      var remote = this.getRemoteState();
      var device =
        (this.getDevices().filter(function (d) {
          return d.connected;
        })[0] ||
          null);
      var pairingCode = null;
      if (
        activeProvider &&
        activeProvider.name === "roku" &&
        activeProvider.getPairingCode
      ) {
        pairingCode = activeProvider.getPairingCode() || null;
      }
      var deviceName = device ? device.name : null;
      if (!deviceName && pairingCode) {
        deviceName = "Roku (pairing " + pairingCode + ")";
      } else if (
        !deviceName &&
        activeProvider &&
        activeProvider.name === "roku" &&
        target === CastingTarget.REMOTE
      ) {
        deviceName = "Roku (cloud session)";
      }
      return {
        uiState: getUiState(),
        target: target,
        provider: activeProvider ? activeProvider.name : null,
        deviceName: deviceName,
        device: device,
        pairingCode: pairingCode,
        hasLoadedMedia: hasLoadedMedia,
        remote: remote,
        lastError: lastError,
        groups: getDevicesGrouped(),
        whiteLabel: Object.assign({}, whiteLabel),
      };
    },

    getEventLog: function () {
      return eventLog.slice();
    },

    getDiagnostics: function () {
      return {
        target: target,
        uiState: getUiState(),
        providers: getProvidersSnapshot(),
        devices: this.getDevices(),
        groups: getDevicesGrouped(),
        whiteLabel: Object.assign({}, whiteLabel),
        activeProvider: activeProvider ? activeProvider.name : null,
        connection: activeProvider
          ? activeProvider.getConnectionState()
          : CastingConnectionState.DISCONNECTED,
        remote: this.getRemoteState(),
        hasLoadedMedia: hasLoadedMedia,
        playbackAttached: !!engine,
        sync: {
          lastRemoteState: lastRemoteState,
          local: localSnapshot(),
        },
        eventLog: eventLog.slice(0, 20),
      };
    },

    /**
     * Roku cloud pairing — creates a session + pairing code for the sideloaded
     * channel. Does not invent devices. CastingManager.start("roku") still rejects
     * discovery-based connect (requiresReceiverApp without going through this API).
     *
     * @param {object} [media]
     * @returns {Promise<object>} cloud session with pairingCode
     */
    startRokuCloudPairing: function (media) {
      var provider = null;
      for (var i = 0; i < providers.length; i++) {
        if (providers[i].name === "roku") {
          provider = providers[i];
          break;
        }
      }
      if (!provider || typeof provider.startCloudPairing !== "function") {
        return Promise.reject(new Error("Roku cloud pairing is not available"));
      }
      var snap = localSnapshot();
      var payload = Object.assign({}, mediaFromLocal(), media || {}, {
        currentTime: (media && media.currentTime) || snap.currentTime,
        position: (media && media.position) || snap.currentTime,
        volume: snap.volume,
        muted: snap.muted,
      });
      connecting = true;
      lastError = null;
      return provider
        .startCloudPairing(payload)
        .then(function (session) {
          activeProvider = provider;
          target = CastingTarget.REMOTE;
          connecting = false;
          hasLoadedMedia = !!(
            payload.src ||
            payload.mediaRef ||
            (payload.media && payload.media.mediaRef)
          );
          emit(CastingEvent.SESSION_STARTED, {
            provider: "roku",
            pairingCode: session && session.pairingCode,
            sessionId: session && session.sessionId,
            cloud: true,
          });
          emit(CastingEvent.TARGET_CHANGED, { target: target });
          return session;
        })
        .catch(function (err) {
          connecting = false;
          lastError = err;
          throw err;
        });
    },

    /**
     * Start casting with a named provider.
     * Media is optional — connect-only yields connected/idle until handoffContent.
     * Receiver-required providers reject discovery connect — use startRokuCloudPairing.
     */
    start: function (providerName, deviceId) {
      var provider = null;
      for (var i = 0; i < providers.length; i++) {
        if (providers[i].name === providerName) {
          provider = providers[i];
          break;
        }
      }
      if (!provider) {
        return Promise.reject(
          new Error("Unknown casting provider: " + providerName)
        );
      }
      var caps = provider.getCapabilities ? provider.getCapabilities() : {};
      if (caps.requiresReceiverApp || caps.requiresNativeApp) {
        return Promise.reject(
          new Error(
            providerName +
              " requires a native receiver / app and is not operational from this browser"
          )
        );
      }
      if (provider.isAvailable && !provider.isAvailable()) {
        return Promise.reject(new Error(providerName + " is not available"));
      }

      var snap = localSnapshot();
      connecting = true;
      lastError = null;
      // Pause local while remote takes over (AirPlay keeps same element)
      if (providerName !== "airplay" && engine) {
        engine.pause();
      }

      var self = this;
      return provider
        .connect(deviceId)
        .then(function () {
          activeProvider = provider;
          target = CastingTarget.REMOTE;
          connecting = false;
          var media = mediaFromLocal();
          var loadPromise =
            provider.loadMedia && media.src
              ? provider.loadMedia(media).then(function () {
                  hasLoadedMedia = true;
                })
              : Promise.resolve();
          return loadPromise.then(function () {
            if (provider.synchronizeState && media.src) {
              return provider.synchronizeState(snap);
            }
            emit(CastingEvent.TARGET_CHANGED, { target: target });
            return self.getRemoteState();
          });
        })
        .catch(function (err) {
          connecting = false;
          lastError = err;
          throw err;
        });
    },

    /**
     * App-level entry: pick preferred available provider and start (connect ± media).
     */
    startPreferred: function (deviceId) {
      var name = pickPreferredProviderName();
      if (!name) {
        return Promise.reject(new Error("No cast devices available"));
      }
      return this.start(name, deviceId);
    },

    stop: function () {
      var provider = activeProvider;
      var resumeAt =
        (lastRemoteState && lastRemoteState.currentTime) ||
        localSnapshot().currentTime;
      if (!provider) {
        target = CastingTarget.LOCAL;
        hasLoadedMedia = false;
        connecting = false;
        return Promise.resolve();
      }
      return provider.disconnect().then(function () {
        activeProvider = null;
        target = CastingTarget.LOCAL;
        hasLoadedMedia = false;
        connecting = false;
        if (engine && resumeAt != null) {
          engine.seek(resumeAt);
        }
        emit(CastingEvent.TARGET_CHANGED, { target: target });
      });
    },

    play: function () {
      if (target === CastingTarget.REMOTE && activeProvider) {
        return activeProvider.play();
      }
      return engine ? engine.play() : Promise.resolve();
    },

    pause: function () {
      if (target === CastingTarget.REMOTE && activeProvider) {
        return activeProvider.pause();
      }
      if (engine) engine.pause();
      return Promise.resolve();
    },

    seek: function (t) {
      if (target === CastingTarget.REMOTE && activeProvider) {
        return activeProvider.seek(t);
      }
      if (engine) engine.seek(t);
      return Promise.resolve();
    },

    setVolume: function (v) {
      if (
        target === CastingTarget.REMOTE &&
        activeProvider &&
        activeProvider.setVolume
      ) {
        return activeProvider.setVolume(v);
      }
      if (engine) engine.setVolume(v);
      return Promise.resolve();
    },

    setMuted: function (m) {
      if (
        target === CastingTarget.REMOTE &&
        activeProvider &&
        activeProvider.setMuted
      ) {
        return activeProvider.setMuted(m);
      }
      if (engine) engine.setMuted(m);
      return Promise.resolve();
    },

    /** Route UI transport through manager when casting */
    routeTransport: function () {
      return target === CastingTarget.REMOTE;
    },

    on: emitter.on,
    off: emitter.off,

    /**
     * Tear down providers and end session. App-level host should only call this
     * on full app teardown — never on VideoPlayer unmount.
     */
    destroy: function () {
      clearProviderListeners();
      providers.forEach(function (p) {
        if (p.destroy) p.destroy();
      });
      if (screenMirror && screenMirror.destroy) {
        screenMirror.destroy();
      }
      screenMirror = null;
      emitter.removeAllListeners();
      activeProvider = null;
      target = CastingTarget.LOCAL;
      hasLoadedMedia = false;
      connecting = false;
      engine = null;
      tracks = null;
    },
  };
}

export default createCastingManager;
